import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { resetSchoolBrand } from '../hooks/useSchoolBrand';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  // Use 'accessToken' key (new system) but fall back to 'token' (old system)
  const [token,   setToken]   = useState(
    localStorage.getItem('accessToken') || localStorage.getItem('token')
  );
  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Try /auth/me first (new), fall back to /auth/profile (old)
      api.get('/auth/me')
        .then(res => {
          setUser(res.data);
        })
        .catch(() => {
          // Clear everything — token invalid or expired
          localStorage.removeItem('accessToken');
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });

      // New auth returns { accessToken, user }
      // Old auth returns { token, ...userData }
      const newToken = res.data.accessToken || res.data.token;
      const userData = res.data.user || (() => {
        const { token: _t, accessToken: _a, ...rest } = res.data;
        return rest;
      })();

      // Store under new key, also clear old key
      localStorage.setItem('accessToken', newToken);
      localStorage.removeItem('token');
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      setToken(newToken);
      setUser(userData);

      toast.success('Logged in successfully');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
      return false;
    }
  };

  const logout = async () => {
    try {
      // Tell server to revoke refresh token
      await api.post('/auth/logout').catch(() => {});
    } catch {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    resetSchoolBrand(); // clear cached brand on logout
    toast.success('Logged out');
    setTimeout(() => { window.location.href = '/login'; }, 300);
  };

  // loginWithToken — used by owner panel to impersonate a branch admin
  const loginWithToken = async (token, userData) => {
    localStorage.setItem('accessToken', token);
    localStorage.removeItem('token');
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setToken(token);
    setUser(userData);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#f0f2f7',
        fontFamily: "'Sora', sans-serif", flexDirection: 'column', gap: 12,
      }}>
        <div style={{
          width: 40, height: 40,
          border: '3px solid #e4e7f0', borderTopColor: '#10c98e',
          borderRadius: '50%', animation: 'spin 0.7s linear infinite',
        }}/>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontSize: '.82rem', color: '#9094a8', fontWeight: 500 }}>Loading…</div>
      </div>
    );
  }

  const value = { user, login, logout, loading, loginWithToken };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};